import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type BadgeTier = "bronze" | "silver" | "gold";
export interface PersonalityBadge {
  key: string;
  label: string;
  tier: BadgeTier;
  score: number;
}

export interface TravelPersonality {
  archetype: string | null;
  archetype_confidence: number;
  axis_adventure: number;
  axis_culture: number;
  axis_planning: number;
  axis_luxury: number;
  axis_party: number;
  top_interests: string[];
  badges?: PersonalityBadge[];
  signal_count: number;
  last_computed_at: string | null;
}

export function useTravelPersonality(autoComputeIfMissing = true) {
  const [personality, setPersonality] = useState<TravelPersonality | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isComputing, setIsComputing] = useState(false);

  const fetchPersonality = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.is_anonymous) {
      setPersonality(null);
      setIsLoading(false);
      return null;
    }
    const { data } = await supabase
      .from("travel_personalities" as any)
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    if (data) {
      const p = data as any;
      setPersonality({
        archetype: p.archetype,
        archetype_confidence: p.archetype_confidence ?? 0,
        axis_adventure: p.axis_adventure ?? 50,
        axis_culture: p.axis_culture ?? 50,
        axis_planning: p.axis_planning ?? 50,
        axis_luxury: p.axis_luxury ?? 50,
        axis_party: p.axis_party ?? 50,
        top_interests: Array.isArray(p.top_interests) ? p.top_interests : [],
        signal_count: p.signal_count ?? 0,
        last_computed_at: p.last_computed_at,
      });
    } else {
      setPersonality(null);
    }
    setIsLoading(false);
    return data;
  }, []);

  const compute = useCallback(async () => {
    setIsComputing(true);
    try {
      const { data, error } = await supabase.functions.invoke("compute-personality");
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      await fetchPersonality();
      return data;
    } finally {
      setIsComputing(false);
    }
  }, [fetchPersonality]);

  useEffect(() => {
    (async () => {
      const existing = await fetchPersonality();
      if (!existing && autoComputeIfMissing) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && !user.is_anonymous) await compute();
      }
    })();
  }, [fetchPersonality, compute, autoComputeIfMissing]);

  return { personality, isLoading, isComputing, refetch: fetchPersonality, compute };
}

/** Fire-and-forget recompute after key user actions. Safe to call without awaiting. */
export async function triggerPersonalityRecompute() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.is_anonymous) return;
    await supabase.functions.invoke("compute-personality");
  } catch (e) {
    console.warn("personality recompute failed", e);
  }
}
